import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  Square,
  Play,
  RefreshCw,
  Search,
  X,
  Mic,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

import Recorder from "recorder-js";

const genericOptions = [
  { id: "generic-poly", type: "generic", name: "Polyester", density: 0.00015 },
  { id: "generic-nylon", type: "generic", name: "Nylon", density: 0.00012 },
  { id: "generic-gut", type: "generic", name: "Natural Gut", density: 0.0001 },
  { id: "generic-hybrid", type: "generic", name: "Hybrid", density: 0.00013 },
];

const AudioRecorder = ({ onAudioProcessed }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [audioURL, setAudioURL] = useState("");
  const [audioBlob, setAudioBlob] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [frequencyData, setFrequencyData] = useState(null);
  const [usedTension, setUsedTension] = useState(null);
  const [usedFrequency, setUsedFrequency] = useState(null);
  const [stringType, setStringType] = useState("polyester");
  const [allSpecificStrings, setAllSpecificStrings] = useState([]);
  const [selectedItem, setSelectedItem] = useState(genericOptions[0]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  const dropdownRef = useRef(null);

  // Novos estados para seletores
  const [headSize, setHeadSize] = useState("standard"); // midsize, standard, oversized, personalizado
  const [stringPattern, setStringPattern] = useState("16x19"); // 16x19, 18x20, outro // padrão
  const [customHeadSize, setCustomHeadSize] = useState(""); // valor personalizado em sq in

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  // Recorder.js refs for iOS
  const recorderJSRef = useRef(null);
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  useEffect(() => {
    fetch("/tennis_strings.json")
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        const stringsWithId = data.map((s, index) => ({
          ...s,
          id: `${s.brand}-${s.name}-${s.gauge}-${index}`,
          type: "specific",
        }));
        setAllSpecificStrings(stringsWithId);
      })
      .catch((error) => {
        console.error("Error fetching tennis strings:", error);
        setErrorMessage("Could not load string list.");
      });
  }, []);

  const filteredSpecificStrings = useMemo(() => {
    if (!searchQuery) return allSpecificStrings;
    const lowerCaseQuery = searchQuery.toLowerCase();
    return allSpecificStrings.filter(
      (s) =>
        s.name.toLowerCase().includes(lowerCaseQuery) ||
        s.brand.toLowerCase().includes(lowerCaseQuery) ||
        s.gauge.toLowerCase().includes(lowerCaseQuery) ||
        (s.material && s.material.toLowerCase().includes(lowerCaseQuery))
    );
  }, [searchQuery, allSpecificStrings]);

  const dropdownOptions = useMemo(() => {
    return [...genericOptions, ...filteredSpecificStrings];
  }, [filteredSpecificStrings]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownVisible(false);
      }
    }
    if (isDropdownVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownVisible]);

  // Se selecionou uma corda do JSON, usa a densidade dela
  const getCurrentDensity = useCallback(() => {
    if (!selectedItem || typeof selectedItem.density !== "number") {
      console.warn(
        "Selected item has invalid density, using default polyester density:",
        selectedItem
      );
      return genericOptions[0].density;
    }
    return selectedItem.density;
  }, [selectedItem]);

  const handleSelectItem = (item) => {
    setSelectedItem(item);
    setIsDropdownVisible(false);
    setSearchQuery("");
  };

  const toggleDropdown = () => {
    setIsDropdownVisible(!isDropdownVisible);
    if (isDropdownVisible) {
      setSearchQuery("");
    }
  };

  const displayString =
    selectedItem.type === "generic"
      ? `${selectedItem.name} (Generic)`
      : `${selectedItem.brand} - ${selectedItem.name} (${selectedItem.gauge})`;
  const showStringDetails = selectedItem.type === "specific";

  // Recalcular tensão ao mudar stringType, mantendo a frequência
  useEffect(() => {
    if (usedFrequency && usedFrequency > 0) {
      const stringLength = 0.65;
      const linearDensity = getCurrentDensity();
      const tension =
        4 *
        Math.pow(stringLength, 2) *
        Math.pow(usedFrequency, 2) *
        linearDensity *
        0.224809 *
        1.15;
      setUsedTension(tension);
    }
  }, [usedFrequency, getCurrentDensity]);

  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  const startRecording = async () => {
    try {
      setErrorMessage("");
      audioChunksRef.current = [];
      setFrequencyData(null);

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setErrorMessage(
          "Your browser does not support microphone access. Try using the latest version of Chrome, Firefox, or Safari."
        );
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });

      // Configurar o contexto de áudio para análise em tempo real
      audioContextRef.current = new (window.AudioContext ||
        window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 4096; // Aumentado para melhor resolução de frequência
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);

      if (isIOS) {
        // Use recorder-js for iOS (npm package, not original Recorder.js)
        console.log("Initializing recorder-js for iOS");
        recorderJSRef.current = new Recorder(audioContextRef.current);
        await recorderJSRef.current.init(stream);
        await recorderJSRef.current.start();
        setIsRecording(true);
        analyzeAudio();
        return;
      }

      // Configurar o gravador de mídia para outros navegadores
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
      });
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        const audioUrl = URL.createObjectURL(audioBlob);
        setAudioURL(audioUrl);
        setAudioBlob(audioBlob);
      };
      mediaRecorderRef.current.start();
      setIsRecording(true);
      analyzeAudio();
    } catch (error) {
      console.error("Error starting recording:", error);
      if (
        error.name === "NotAllowedError" ||
        error.name === "PermissionDeniedError"
      ) {
        if (isSafari) {
          setErrorMessage(
            "Could not access the microphone in Safari. Go to Safari > Preferences > Websites > Microphone and allow access for this site."
          );
        } else {
          setErrorMessage(
            "Could not access the microphone. Please check your browser permissions."
          );
        }
      } else if (isSafari) {
        setErrorMessage(
          "Safari may block microphone access if you have previously denied permission. Go to Safari > Preferences > Websites > Microphone and allow access for this site."
        );
      } else {
        setErrorMessage(
          "Could not access the microphone. Please check your browser permissions."
        );
      }
    }
  };

  const stopRecording = async () => {
    console.log("Stop button pressed"); // Debug log
    if (isIOS && recorderJSRef.current && isRecording) {
      try {
        console.log("Calling recorderJSRef.current.stop()");
        const { blob, buffer } = await recorderJSRef.current.stop();
        console.log("recorderJSRef.current.stop() finished, got blob:", blob);
        if (!blob) {
          console.error("recorder-js stop: blob is null or undefined");
          alert("Failed to export audio.");
          return;
        }
        const audioUrl = URL.createObjectURL(blob);
        setAudioURL(audioUrl);
        setAudioBlob(blob);
        setIsRecording(false);
      } catch (err) {
        console.error("Error in iOS stop/export:", err);
        alert(
          "Error stopping or exporting audio on iOS. See console for details."
        );
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      return;
    }
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      // Parar todas as faixas de áudio
      mediaRecorderRef.current.stream
        .getTracks()
        .forEach((track) => track.stop());
      // Cancelar o frame de animação
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    }
  };

  const analyzeAudio = () => {
    if (!analyserRef.current || !isRecording) return;

    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    analyserRef.current.getByteFrequencyData(dataArray);

    // Encontrar a frequência dominante em tempo real
    const sampleRate = audioContextRef.current.sampleRate;
    const maxIndex = findMaxFrequencyIndex(dataArray);
    const dominantFrequency =
      (maxIndex * sampleRate) / analyserRef.current.fftSize;

    // Atualizar dados de frequência para visualização
    setFrequencyData({
      frequency: dominantFrequency,
      magnitude: dataArray[maxIndex],
    });

    // Continuar a análise enquanto estiver gravando
    animationFrameRef.current = requestAnimationFrame(analyzeAudio);
  };

  const findMaxFrequencyIndex = (dataArray) => {
    // Ignorar frequências muito baixas (abaixo de 80Hz) que podem ser ruído
    const minIndex = Math.floor(
      (80 * analyserRef.current.fftSize) / audioContextRef.current.sampleRate
    );

    // Ignorar frequências muito altas (acima de 1000Hz) que provavelmente não são da corda
    const maxIndex = Math.floor(
      (1000 * analyserRef.current.fftSize) / audioContextRef.current.sampleRate
    );

    let maxValue = 0;
    let peakIndex = 0;

    for (let i = minIndex; i < maxIndex; i++) {
      if (dataArray[i] > maxValue) {
        maxValue = dataArray[i];
        peakIndex = i;
      }
    }

    return peakIndex;
  };

  const processAudio = async () => {
    if (!audioBlob) return;

    setIsProcessing(true);

    try {
      // Parâmetros globais para tensão
      // Comprimento médio padrão
      let stringLength = 0.65; // metros (padrão)
      // Ajuste pelo tamanho da cabeça
      if (headSize === "midsize") stringLength = 0.63;
      if (headSize === "oversized") stringLength = 0.68;
      if (
        headSize === "custom" &&
        customHeadSize &&
        !isNaN(Number(customHeadSize))
      ) {
        // Standard relation: 100 sq in => 0.65m; proportional adjustment
        const sqin = Math.max(85, Math.min(120, Number(customHeadSize)));
        stringLength = 0.65 * Math.sqrt(sqin / 100);
      }
      // String pattern factor
      let patternFactor = 1.0;
      if (stringPattern === "18x20") patternFactor = 1.03;
      if (stringPattern === "other") patternFactor = 0.97;
      // Linear density
      const linearDensity = selectedItem.density; // kg/m (based on string type)
      console.log({ linearDensity, selectedItem });
      const MIN_TENSION = 25; // lbs
      const MAX_TENSION = 65; // lbs

      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      const audioData = await audioBlob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(audioData);

      // Extrair dados para análise de frequência
      const channelData = audioBuffer.getChannelData(0);
      const sampleRate = audioBuffer.sampleRate;

      // Analyze the entire recorded audio to maximize dominant frequency detection
      // If the audio is too quiet, still warn the user
      const rmsAll = Math.sqrt(
        channelData.reduce((sum, v) => sum + v * v, 0) / channelData.length
      );
      if (rmsAll < 0.001) {
        setErrorMessage(
          "Sound too low. Try recording again, bringing the microphone closer to the string."
        );
        setIsProcessing(false);
        return;
      }
      // FFT de todo o áudio gravado
      function computeFFT(buffer, sampleRate) {
        // FFT simples (zero padding para potência de 2)
        const N = Math.pow(2, Math.ceil(Math.log2(buffer.length)));
        const re = new Float32Array(N);
        const im = new Float32Array(N);
        for (let i = 0; i < buffer.length; i++) re[i] = buffer[i];
        // FFT radix-2 (usa WebAudio API ou implementação simples)
        // Aqui usamos uma implementação básica (DFT) para garantir compatibilidade
        // Se quiser mais performance, pode usar uma lib como dsp.js
        for (let k = 0; k < N; k++) {
          for (let n = 0; n < buffer.length; n++) {
            // Corrigido!
            const angle = (2 * Math.PI * k * n) / N;
            re[k] += buffer[n] * Math.cos(angle);
            im[k] -= buffer[n] * Math.sin(angle);
          }
        }
        const spectrum = new Float32Array(N / 2);
        for (let k = 0; k < N / 2; k++) {
          spectrum[k] = Math.sqrt(re[k] * re[k] + im[k] * im[k]);
        }
        // Frequências correspondentes
        const frequencies = Array.from(
          { length: N / 2 },
          (_, k) => (k * sampleRate) / N
        );
        return { spectrum, frequencies };
      }

      // FFT on up to 16384 samples from the center of the audio
      const MAX_FFT_SIZE = 16384;
      let start = 0;
      let end = channelData.length;
      if (channelData.length > MAX_FFT_SIZE) {
        start = Math.floor((channelData.length - MAX_FFT_SIZE) / 2);
        end = start + MAX_FFT_SIZE;
      }
      const fftSlice = channelData.slice(start, end);
      const { spectrum, frequencies } = computeFFT(fftSlice, sampleRate);
      // Log spectrum for debugging
      // console.log('spectrum (0-50):', Array.from(spectrum.slice(0, 50)));
      // Find all relevant peaks within the frequency range of interest
      // Consider only frequencies that result in tensions between 25 and 65 lbs
      let validPeaks = [];
      const maxSpectrum = Math.max(...spectrum);
      const threshold = maxSpectrum * 0.05; // 5% of the max (more sensitive)
      // Function to calculate tension (using already declared variables)
      // (Removed duplicate declaration)
      for (let i = 1; i < spectrum.length - 1; i++) {
        const freq = frequencies[i];
        const tension = calcTensionLbs(freq);
        if (
          tension >= MIN_TENSION &&
          tension <= MAX_TENSION &&
          spectrum[i] > spectrum[i - 1] &&
          spectrum[i] > spectrum[i + 1] &&
          spectrum[i] > threshold
        ) {
          validPeaks.push({
            frequency: freq,
            magnitude: spectrum[i],
            index: i,
            tension: tension,
          });
        }
      }
      // Sort by descending magnitude
      validPeaks.sort((a, b) => b.magnitude - a.magnitude);
      // Log all peaks above 400 Hz for debugging
      // console.log('All validPeaks above 400Hz:', validPeaks.filter(p => p.frequency > 400).map(p => p.frequency.toFixed(1)));
      // Priorize pico entre 500 Hz e 1200 Hz
      const preferredPeak = validPeaks.find(
        (p) => p.frequency >= 500 && p.frequency <= 1200
      );
      const mainPeak = preferredPeak ||
        validPeaks[0] || { frequency: 0, magnitude: 0 };

      // Use apenas o valor do bin do pico principal
      const dominantFrequency = mainPeak.frequency;

      // Salvar picos válidos para UI (permitir escolha futura)
      setFrequencyData({
        spectrum: Array.from(spectrum),
        frequencies: Array.from(frequencies),
        validPeaks,
        usedFrequency: dominantFrequency,
      });
      // Só atualize a frequência detectada ao processar áudio, não ao trocar stringType
      setUsedFrequency(dominantFrequency);
      // Armazene também os picos válidos para seleção manual futura
      // (mantido no estado frequencyData)

      // (Opcional) log para debug
      // console.log('validPeaks:', validPeaks.map(p => p.frequency.toFixed(1)));
      // console.log('Frequência usada:', dominantFrequency);

      // Calcular a tensão com base na frequência usando a fórmula da pesquisa
      // T = 4L² · f² · μ
      let tension = 0;
      if (dominantFrequency > 0) {
        tension =
          4 *
          Math.pow(stringLength, 2) *
          Math.pow(dominantFrequency, 2) *
          linearDensity *
          0.224809 *
          1.15;
      }
      setUsedTension(tension);

      // Função para calcular tensão já corrigida (usando variáveis já declaradas)
      // Apenas uma declaração global de stringLength, linearDensity
      function calcTensionLbs(freq) {
        const tension =
          4 * Math.pow(stringLength, 2) * Math.pow(freq, 2) * linearDensity;
        const tensionLbs = tension * 0.224809;
        const correctionFactor = 1.15;
        return tensionLbs * correctionFactor;
      }

      let usedFrequency = dominantFrequency;
      let usedTension = calcTensionLbs(dominantFrequency);

      // Se a tensão está fora da faixa aceitável, tente harmonicos
      if (usedTension < MIN_TENSION || usedTension > MAX_TENSION) {
        // Tente harmônicos superiores
        for (let divisor of [2, 3]) {
          const freq = dominantFrequency * divisor;
          const t = calcTensionLbs(freq);
          if (t >= MIN_TENSION && t <= MAX_TENSION) {
            usedFrequency = freq;
            usedTension = t;
            break;
          }
        }
        // Tente sub-harmônicos
        if (usedTension < MIN_TENSION) {
          for (let multiplier of [2, 3]) {
            const freq = dominantFrequency / multiplier;
            const t = calcTensionLbs(freq);
            if (t >= MIN_TENSION && t <= MAX_TENSION) {
              usedFrequency = freq;
              usedTension = t;
              break;
            }
          }
        }
      }

      // Enviar resultado para o componente pai
      if (onAudioProcessed) {
        onAudioProcessed({
          frequency: usedFrequency,
          tension: usedTension,
        });
      }
    } catch (error) {
      console.error("Error processing audio:", error);
      setErrorMessage("Error processing the audio. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const resetRecording = () => {
    setAudioURL("");
    setAudioBlob(null);
    setErrorMessage("");
    setFrequencyData(null);
    if (onAudioProcessed) {
      onAudioProcessed(null);
    }
  };

  return (
    <div className="flex flex-col items-center p-4 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-bold mb-4">Sound Recorder</h2>

      <div className="w-full mb-4" ref={dropdownRef}>
        <label
          htmlFor="stringSelector"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          String Type
        </label>
        <button
          id="stringSelector"
          onClick={toggleDropdown}
          className="w-full p-2 border border-gray-300 rounded-md shadow-sm bg-white text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <span>{displayString}</span>
          {isDropdownVisible ? (
            <ChevronUp className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {isDropdownVisible && (
          <div className="relative mt-1">
            <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-72 overflow-y-auto">
              <div className="p-2 sticky top-0 bg-white z-10 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search by name or brand..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full p-2 pl-9 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    autoComplete="off"
                    autoFocus
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>
              <ul className="pt-1 pb-1">
                {dropdownOptions.map((item) => (
                  <li
                    key={item.id}
                    onClick={() => handleSelectItem(item)}
                    className={`px-4 py-2 cursor-pointer hover:bg-blue-100 ${
                      selectedItem?.id === item.id
                        ? "bg-blue-50 font-medium"
                        : ""
                    }`}
                  >
                    {item.type === "generic"
                      ? `${item.name} (Generic)`
                      : `${item.brand} - ${item.name} (${item.gauge}, ${
                          item.material || "N/A"
                        })`}
                  </li>
                ))}
                {searchQuery &&
                  filteredSpecificStrings.length === 0 &&
                  genericOptions.every(
                    (opt) =>
                      opt.name
                        .toLowerCase()
                        .indexOf(searchQuery.toLowerCase()) === -1
                  ) && (
                    <li className="px-4 py-2 text-gray-500">
                      No strings found.
                    </li>
                  )}
                {!searchQuery && allSpecificStrings.length === 0 && (
                  <li className="px-4 py-2 text-gray-500">
                    Loading specific strings...
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}

        {showStringDetails && (
          <div className="mb-4 mt-4 p-3 border border-blue-200 bg-blue-50 rounded-md text-sm text-gray-700 space-y-1">
            <p>
              <span className="font-medium">Brand:</span> {selectedItem.brand}
            </p>
            <p>
              <span className="font-medium">Model:</span> {selectedItem.name}
            </p>
            <p>
              <span className="font-medium">Type:</span>{" "}
              {selectedItem.material || "N/A"}
            </p>
            <p>
              <span className="font-medium">Gauge:</span> {selectedItem.gauge} (
              {selectedItem.thickness ? `${selectedItem.thickness} mm` : "N/A"})
            </p>
            <p>
              <span className="font-medium">Density:</span>{" "}
              {selectedItem.density ? `${selectedItem.density} kg/m` : "N/A"}{" "}
              (Used for calculation)
            </p>
            {selectedItem.tension_range && (
              <p>
                <span className="font-medium">Rec. Tension:</span>{" "}
                {(selectedItem.tension_range.min * 2.20462).toFixed(1)} -{" "}
                {(selectedItem.tension_range.max * 2.20462).toFixed(1)} lbs
              </p>
            )}
          </div>
        )}
      </div>

      <div className="w-full mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Head Size
        </label>
        <select
          value={headSize}
          onChange={(e) => setHeadSize(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded-md"
          disabled={isRecording || isProcessing}
        >
          <option value="midsize">Midsize (&lt;97 sq in)</option>
          <option value="standard">Standard (97-105 sq in)</option>
          <option value="oversized">Oversized (&gt;105 sq in)</option>
          <option value="custom">Custom</option>
        </select>
        {headSize === "custom" && (
          <input
            type="number"
            min="85"
            max="120"
            step="1"
            value={customHeadSize}
            onChange={(e) => setCustomHeadSize(e.target.value)}
            placeholder="Head Size (sq in)"
            className="mt-2 w-full p-2 border border-gray-300 rounded-md"
            disabled={isRecording || isProcessing}
          />
        )}
      </div>
      <div className="w-full mb-2 text-xs text-gray-600">
        The racket head size influences the measurement: larger heads (in sq in)
        result in longer strings, which lowers the frequency detected for the
        same tension. Enter the exact value in square inches for higher
        accuracy.
      </div>
      <div className="w-full mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          String Pattern
        </label>
        <select
          value={stringPattern}
          onChange={(e) => setStringPattern(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded-md"
          disabled={isRecording || isProcessing}
        >
          <option value="16x19">16 x 19 (Open)</option>
          <option value="18x20">18 x 20 (Dense)</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div className="flex flex-col items-center w-full">
        <div className="flex justify-center space-x-4 mb-4">
          {!isRecording ? (
            <button
              onClick={startRecording}
              className="flex items-center justify-center p-4 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
              disabled={isProcessing}
            >
              <Mic size={24} />
            </button>
          ) : (
            <button
              onClick={() => {
                console.log("Stop button clicked");
                stopRecording();
              }}
              className="flex items-center justify-center p-4 bg-gray-700 text-white rounded-full hover:bg-gray-800 transition-colors"
            >
              <Square size={24} />
            </button>
          )}

          {audioURL && !isRecording && (
            <>
              <button
                onClick={() => {
                  const audio = new Audio(audioURL);
                  audio.play();
                }}
                className="flex items-center justify-center p-4 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors"
                disabled={isProcessing}
              >
                <Play size={24} />
              </button>

              <button
                onClick={resetRecording}
                className="flex items-center justify-center p-4 bg-gray-500 text-white rounded-full hover:bg-gray-600 transition-colors"
                disabled={isProcessing}
              >
                <RefreshCw size={24} />
              </button>
            </>
          )}
        </div>

        {frequencyData && isRecording && (
          <div className="w-full mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Detected Frequency:</span>
              <span className="font-medium">
                {frequencyData.frequency.toFixed(2)} Hz
              </span>
            </div>
            <div className="mt-2 h-4 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all duration-300"
                style={{
                  width: `${Math.min(
                    100,
                    (frequencyData.magnitude / 255) * 100
                  )}%`,
                }}
              ></div>
            </div>
          </div>
        )}

        {audioURL && !isRecording && (
          <div className="w-full mb-4">
            <audio src={audioURL} controls className="w-full" />
          </div>
        )}

        {audioURL && !isRecording && (
          <div className="w-full">
            <div className="flex items-center mb-2"></div>

            <button
              onClick={processAudio}
              className="w-full px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
              disabled={isProcessing}
            >
              {isProcessing ? "Processing..." : "Analyze Sound"}
            </button>
          </div>
        )}

        {isRecording && (
          <div className="flex items-center mt-2">
            <div className="w-3 h-3 bg-red-500 rounded-full mr-2 animate-pulse"></div>
            <span>Recording...</span>
          </div>
        )}

        {errorMessage && (
          <div className="mt-4 p-2 bg-red-100 text-red-700 rounded-md">
            {errorMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioRecorder;
